/**
 * ChatPanel - режим чата виджета
 *
 * Структура:
 * - WidgetHeader с переключателем режимов voice/chat и контролами
 * - Область сообщений (MessagesArea)
 * - Error banner (опционально)
 * - Bottom controls: встроенный input или renderControls для кастомных контролов
 */

import { useState, useRef, useCallback, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { X } from '@phosphor-icons/react';

// Swipe threshold in pixels - how far user needs to swipe to trigger action
const SWIPE_THRESHOLD = 50;
import { WidgetHeader } from './WidgetHeader';
import { MessagesArea } from './MessagesArea';
import { BarEqualizer } from './BarEqualizer';
import { AnimatedDots } from './AnimatedDots';
import { ArrowIcon } from './icons';
import { WidgetLink } from './WidgetLink';
import { DEFAULT_TERMS_URL, DEFAULT_PRIVACY_URL } from './legalDefaults';
import { PoweredBy } from './PoweredBy';
import { WidgetDivider } from './WidgetDivider';
import { Suggestions } from './Suggestions';
import { t } from './translations';
import {
  getStatusText,
  themePropType,
  connectionStatusPropType,
  languagePropType,
  widgetModePropType,
  widgetStatePropType,
  messagePropType,
} from './utils';
import './ChatPanel.css';

export const ChatPanel = memo(({
  // Header props
  title = 'Assistant',
  isSessionActive = false,
  isSpeaking = false,
  // Mode toggle
  showModeToggle = true,
  currentMode = 'chat',
  onModeChange,
  // Speaker controls
  speakerMuted = false,
  onSpeakerToggle,
  showSpeakerButton = true,
  // Microphone controls
  micMuted = false,
  onMicToggle,
  showMicButton = true,
  // Window controls
  widgetState = 'normal',
  onMinimize,
  onMaximize,
  onRestore,
  onClose,
  onCollapseToVoice, // NEW: Collapse chat to voice/collapsed
  isDraggable = false,
  onDragStart,
  // Messages props
  messages = [],
  autoScroll = true,
  emptyText = 'Start a conversation',
  // Input props
  onSendMessage,
  // Без явной подписи — из словаря виджета на языке `language` (#1417).
  inputPlaceholder,
  // Bottom controls - render prop для кастомных контролов (если не указан, показывается встроенный input)
  renderControls,
  // Voice controls in chat
  showVoiceControls = false,
  onVoiceClose,
  isUserSpeaking = false,
  userAudioLevel = 0,
  agentAudioLevel = 0,
  connectionStatus = 'connected', // 'connecting' | 'connected' | 'disconnected'
  language = 'en', // см. languagePropType: en, fr, de, lu, el, ru, nl
  // Строки #1431 — на языке витрины только на своём стоке (см. VoiceWidget).
  chromeLanguage = 'en',
  primaryColor,
  theme, // ADDED: Theme object for dark mode support
  glassEffect = false,
  isAccentMode = false, // For BarEqualizer contrast in accent mode
  // Error message
  errorMessage = '',
  // Size
  width,
  height,
  // Mobile flag for swipe behavior
  isMobile = false,
  // Mode switching indicator
  isModeChanging = false,
  // Consent banner
  showConsentBanner = false,
  // Legal URLs
  termsUrl = DEFAULT_TERMS_URL,
  privacyUrl = DEFAULT_PRIVACY_URL,
  // Issue #752: Auto-suggestions
  suggestions = [],
  suggestionsLoading = false,
  suggestionsConfig,
  onSuggestionSelect,
}) => {
  const [inputValue, setInputValue] = useState('');
  const touchStartY = useRef(null);
  const isSwiping = useRef(false);
  // Issue #808: ref to input for auto-blur after send on mobile (hides the keyboard
  // so the page/widget content is visible again; user can tap input to reopen it).
  const inputRef = useRef(null);
  // Issue #808: track whether we've auto-maximized on mobile. After the user's
  // first message/pill tap we expand the chat via the existing (stable) maximize
  // mechanism so there's room to pin the user message to the top and show the
  // agent reply underneath.
  const hasAutoMaximizedRef = useRef(false);

  // Issue #752: Debounced suggestions visibility based on input state
  const [suggestionsHiddenByTyping, setSuggestionsHiddenByTyping] = useState(false);
  const typingDebounceRef = useRef(null);

  useEffect(() => {
    if (inputValue.trim()) {
      // User is typing — hide suggestions immediately
      setSuggestionsHiddenByTyping(true);
      if (typingDebounceRef.current) clearTimeout(typingDebounceRef.current);
    } else {
      // Input cleared — show suggestions after debounce
      typingDebounceRef.current = setTimeout(() => {
        setSuggestionsHiddenByTyping(false);
      }, 300);
    }
    return () => {
      if (typingDebounceRef.current) clearTimeout(typingDebounceRef.current);
    };
  }, [inputValue]);

  const hasSuggestions = suggestions.length > 0 || suggestionsLoading;
  const suggestionsVisible = hasSuggestions && !showVoiceControls && !suggestionsHiddenByTyping;

  // Issue #752: Log when suggestions are hidden
  const prevVisibleRef = useRef(suggestionsVisible);
  useEffect(() => {
    if (prevVisibleRef.current && !suggestionsVisible && hasSuggestions) {
      const reason = suggestionsHiddenByTyping ? 'user_typing' : showVoiceControls ? 'voice_controls' : 'unknown';
      console.log('[Suggestions] hidden:', { reason });
    }
    prevVisibleRef.current = suggestionsVisible;
  }, [suggestionsVisible, hasSuggestions, suggestionsHiddenByTyping, showVoiceControls]);

  // Issue #808: when the chat view becomes visible on mobile with existing
  // message history (user reopens the widget after minimize), re-apply the
  // maximized layout — otherwise the widget reopens in the compact 40dvh
  // bottom-sheet state even though the user was in maximized chat before.
  const prevCurrentModeRef = useRef(currentMode);
  useEffect(() => {
    const prev = prevCurrentModeRef.current;
    prevCurrentModeRef.current = currentMode;
    if (
      isMobile &&
      prev !== 'chat' &&
      currentMode === 'chat' &&
      messages.length > 0 &&
      widgetState !== 'maximized' &&
      typeof onMaximize === 'function'
    ) {
      onMaximize();
    }
  }, [currentMode, isMobile, messages.length, widgetState, onMaximize]);

  // Swipe gesture handlers for mobile expand/collapse
  const handleTouchStart = useCallback((e) => {
    if (!isMobile) return;
    touchStartY.current = e.touches[0].clientY;
    isSwiping.current = false;
  }, [isMobile]);

  const handleTouchMove = useCallback((e) => {
    if (!isMobile || touchStartY.current === null) return;

    const currentY = e.touches[0].clientY;
    const deltaY = Math.abs(touchStartY.current - currentY);

    // If moved more than 15px, consider it a swipe and prevent scroll
    if (deltaY > 15) {
      isSwiping.current = true;
      e.preventDefault();
    }
  }, [isMobile]);

  const handleTouchEnd = useCallback((e) => {
    if (!isMobile || touchStartY.current === null) return;

    const touchEndY = e.changedTouches[0].clientY;
    const deltaY = touchStartY.current - touchEndY; // Positive = swipe up

    if (isSwiping.current) {
      e.preventDefault();

      if (deltaY > SWIPE_THRESHOLD && widgetState !== 'maximized' && onMaximize) {
        // Swipe up - expand to full screen
        onMaximize();
      } else if (deltaY < -SWIPE_THRESHOLD && widgetState === 'maximized' && onRestore) {
        // Swipe down - restore to normal size
        onRestore();
      }
    }

    touchStartY.current = null;
    isSwiping.current = false;
  }, [isMobile, widgetState, onMaximize, onRestore]);
  const panelClassName = [
    'chat-panel',
    glassEffect && 'chat-panel--glass',
    widgetState === 'maximized' && 'chat-panel--maximized',
  ].filter(Boolean).join(' ');

  const maybeAutoMaximize = () => {
    // Issue #808: after the user's first interaction on mobile, expand the chat
    // using the existing (stable) maximize mechanism so the pinned user message
    // + streaming agent reply both fit on-screen. Only do it once per session;
    // if the user later drag-collapses we don't fight them.
    if (
      isMobile &&
      !hasAutoMaximizedRef.current &&
      widgetState !== 'maximized' &&
      typeof onMaximize === 'function'
    ) {
      hasAutoMaximizedRef.current = true;
      onMaximize();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim() && onSendMessage) {
      maybeAutoMaximize();
      onSendMessage(inputValue.trim());
      setInputValue('');
      // Issue #808: on mobile, blur the input so the virtual keyboard collapses
      // and the user can see the page content the agent is manipulating.
      // Tapping the input reopens the keyboard naturally.
      if (isMobile && inputRef.current) {
        inputRef.current.blur();
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const statusText = getStatusText({ connectionStatus, isSpeaking, isUserSpeaking, language });

  return (
    <div
      className={panelClassName}
      style={{
        width: width ? `${width}px` : undefined,
        height: height ? `${height}px` : undefined,
      }}
    >
      {/* Mobile drag handle for swipe gestures - inside header area */}
      {isMobile && (
        <div
          className="chat-panel__drag-handle"
          onTouchStartCapture={handleTouchStart}
          onTouchMoveCapture={handleTouchMove}
          onTouchEndCapture={handleTouchEnd}
        >
          <div className="chat-panel__drag-indicator" />
        </div>
      )}
      <WidgetHeader
        language={chromeLanguage}
        title={title}
        isSessionActive={isSessionActive}
        showModeToggle={showModeToggle}
        currentMode={currentMode}
        onModeChange={onModeChange}
        isSpeaking={isSpeaking}
        isUserSpeaking={isUserSpeaking}
        speakerMuted={speakerMuted}
        onSpeakerToggle={onSpeakerToggle}
        showSpeakerButton={showSpeakerButton}
        micMuted={micMuted}
        onMicToggle={onMicToggle}
        showMicButton={showMicButton}
        widgetState={widgetState}
        onMinimize={onMinimize}
        onClose={onClose}
        onCollapseToVoice={onCollapseToVoice}
        isDraggable={isDraggable}
        onDragStart={onDragStart}
        isModeChanging={isModeChanging}
      />

      <MessagesArea
        messages={messages}
        autoScroll={ autoScroll !== undefined ? autoScroll : true}
        emptyText={emptyText}
        language={language}
        className="chat-panel__messages"
        header={showConsentBanner ? (
          <>
            <div className="chat-panel__consent">
              {/* На языке витрины (#1431) — только на своём стоке, у дилеров en. */}
              {t('consentPrefix', chromeLanguage)}{'\u00A0'}<WidgetLink href={termsUrl}>{t('termsOfService', chromeLanguage)}</WidgetLink>
              {' '}{t('and', chromeLanguage)}{'\u00A0'}<WidgetLink href={privacyUrl}>{t('privacyPolicy', chromeLanguage)}</WidgetLink>
            </div>
            <WidgetDivider className="widget-divider--wide" />
          </>
        ) : null}
      />

      {/* Issue #752: Auto-suggestions */}
      {suggestionsConfig?.enabled !== false && (
        <Suggestions
          suggestions={suggestions}
          onSelect={(text, index) => {
            // Issue #808: on mobile, make sure the virtual keyboard doesn't pop up
            // when a suggestion pill is tapped (iOS Safari re-focuses the nearby input
            // during the re-render). Blur both the input and the active element.
            if (isMobile) {
              if (inputRef.current) inputRef.current.blur();
              if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
              }
            }
            maybeAutoMaximize();
            onSuggestionSelect?.(text, index);
          }}
          maxVisible={suggestionsConfig?.maxVisible}
          accentColor={suggestionsConfig?.accentColor}
          variant={suggestionsConfig?.variant}
          visible={suggestionsVisible}
          loading={suggestionsLoading}
        />
      )}

      {/* Error banner */}
      {errorMessage && (
        <div className="chat-panel__error">
          <strong>Error:</strong> {errorMessage}
        </div>
      )}

      {/* Bottom controls area */}
      {showVoiceControls ? (
        // Voice controls - icon + status + close button (same as VoiceBar)
        <div className="chat-panel__voice-controls">
          {/* Animated icon (3 bars) */}
          <div className="voice-controls__icon">
            {/* isAccentMode={false} forces white bars on dark background.
                In voice controls within chat panel, the background is always dark
                (var(--widget-primary)), so we need white bars for contrast. */}
            <BarEqualizer
              isSpeaking={isSpeaking}
              isUserSpeaking={isUserSpeaking}
              userAudioLevel={userAudioLevel}
              agentAudioLevel={agentAudioLevel}
              primaryColor={primaryColor}
              theme={theme}
              isAccentMode={false}
              width={40}
              height={40}
            />
          </div>

          {/* Status text with animated dots */}
          <div className="voice-controls__status">
            {statusText}
            <AnimatedDots />
          </div>

          {/* Close button */}
          <button
            className="voice-controls__close-btn"
            onClick={onVoiceClose}
            onMouseDown={(e) => e.stopPropagation()}
            aria-label={t('endConversation', language)}
            title={t('endConversation', language)}
          >
            <X size={18} weight="regular" />
          </button>
        </div>
      ) : renderControls ? (
        <div className="chat-panel__controls-area">
          {renderControls()}
        </div>
      ) : (
        <div className="chat-panel__input-area">
          <div className="chat-panel__input-wrapper">
            <input
              ref={inputRef}
              type="text"
              className="chat-panel__input"
              placeholder={inputPlaceholder ?? t('typeMessage', language)}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button
              type="button"
              onClick={handleSubmit}
              className={`chat-panel__send-btn ${!inputValue.trim() ? 'chat-panel__send-btn--hidden' : ''}`}
              aria-label={t('sendMessage', chromeLanguage)}
            >
              <ArrowIcon size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Branding link */}
      <PoweredBy language={chromeLanguage} />
    </div>
  );
});

ChatPanel.displayName = 'ChatPanel';

ChatPanel.propTypes = {
  chromeLanguage: languagePropType,
  // Header props
  title: PropTypes.string,
  isSessionActive: PropTypes.bool,
  isSpeaking: PropTypes.bool,
  // Mode toggle
  showModeToggle: PropTypes.bool,
  currentMode: widgetModePropType,
  onModeChange: PropTypes.func,
  // Speaker controls
  speakerMuted: PropTypes.bool,
  onSpeakerToggle: PropTypes.func,
  showSpeakerButton: PropTypes.bool,
  // Microphone controls
  micMuted: PropTypes.bool,
  onMicToggle: PropTypes.func,
  showMicButton: PropTypes.bool,
  // Window controls
  widgetState: widgetStatePropType,
  onMinimize: PropTypes.func,
  onMaximize: PropTypes.func,
  onRestore: PropTypes.func,
  onClose: PropTypes.func,
  onCollapseToVoice: PropTypes.func,
  isDraggable: PropTypes.bool,
  onDragStart: PropTypes.func,
  // Messages props
  messages: PropTypes.arrayOf(messagePropType),
  autoScroll: PropTypes.bool,
  emptyText: PropTypes.string,
  // Input props
  onSendMessage: PropTypes.func,
  inputPlaceholder: PropTypes.string,
  // Bottom controls
  renderControls: PropTypes.func,
  // Voice controls in chat
  showVoiceControls: PropTypes.bool,
  onVoiceClose: PropTypes.func,
  isUserSpeaking: PropTypes.bool,
  userAudioLevel: PropTypes.number,
  agentAudioLevel: PropTypes.number,
  connectionStatus: connectionStatusPropType,
  language: languagePropType,
  primaryColor: PropTypes.string,
  theme: themePropType,
  glassEffect: PropTypes.bool,
  isAccentMode: PropTypes.bool,
  // Error message
  errorMessage: PropTypes.string,
  // Size
  width: PropTypes.number,
  height: PropTypes.number,
  // Mobile flag
  isMobile: PropTypes.bool,
  // Mode switching indicator
  isModeChanging: PropTypes.bool,
  // Consent banner
  showConsentBanner: PropTypes.bool,
  // Legal URLs
  termsUrl: PropTypes.string,
  privacyUrl: PropTypes.string,
  // Issue #752: Auto-suggestions
  suggestions: PropTypes.arrayOf(PropTypes.string),
  suggestionsLoading: PropTypes.bool,
  suggestionsConfig: PropTypes.object,
  onSuggestionSelect: PropTypes.func,
};
